/**
 * Pure reducers that apply a single workspace realtime broadcast onto the local
 * `collections` state, so collaborator edits sync incrementally without a full
 * refetch. Each function takes the current collections array and the event
 * payload and returns a new array (or the same reference when nothing changed).
 *
 * The payload shapes mirror the Phoenix broadcasts:
 *   collection_created  -> { collection }              (format_store, requests: [])
 *   collection_updated  -> { collection }              (format_update, NO requests)
 *   collection_deleted  -> { collection_id }
 *   request_created     -> { request }                 (format_request, has collection_id)
 *   request_updated     -> { request }                 (may have moved collections)
 *   request_deleted     -> { request_id, collection_id }
 *   variables_updated    -> { scope: "collection", collection_id, variable_scopes }
 *
 * The sidebar renders children via getOrderedChildren, which sorts by
 * sort_order, so insertion position within an array does not affect display.
 */
import { hydrateCollectionFromApi, hydrateRequestFromApi } from './collectionFolders';

const sameId = (a, b) => String(a) === String(b);

// Broadcast payloads carry an actor-relative `can_edit` (computed for the user
// who made the change). The receiver's own edit right is what gates their UI,
// so we never adopt the actor's value: on updates we keep the receiver's prior
// `can_edit`; on creates a brand-new entity has no per-entity shares yet, so
// the receiver's workspace-level edit right is the correct value when known.
const withCanEdit = (entity, canEdit) =>
  canEdit === undefined ? entity : { ...entity, can_edit: canEdit };

const priorRequestCanEdit = (collections, id) => {
  for (const c of collections) {
    const found = (c.requests || []).find((r) => sameId(r.id, id));
    if (found) return found.can_edit;
  }
  return undefined;
};

export function applyCollectionCreated(collections, payload, canEdit) {
  const incoming = payload?.collection;
  if (!incoming || incoming.id == null) return collections;
  if (collections.some((c) => sameId(c.id, incoming.id))) return collections;
  return [...collections, withCanEdit(hydrateCollectionFromApi(incoming), canEdit)];
}

export function applyCollectionUpdated(collections, payload) {
  const incoming = payload?.collection;
  if (!incoming || incoming.id == null) return collections;
  // format_update intentionally omits `requests`; spreading it over the
  // existing collection updates metadata while preserving the local requests.
  // `can_edit` is dropped so the receiver's prior value is kept.
  const { can_edit, ...rest } = incoming;
  return collections.map((c) => (sameId(c.id, incoming.id) ? { ...c, ...rest } : c));
}

export function applyCollectionDeleted(collections, payload) {
  const id = payload?.collection_id;
  if (id == null) return collections;
  return collections.filter((c) => !sameId(c.id, id));
}

export function applyRequestCreated(collections, payload, canEdit) {
  const incoming = payload?.request;
  if (!incoming || incoming.id == null) return collections;
  const request = withCanEdit(hydrateRequestFromApi(incoming), canEdit);
  return collections.map((c) => {
    if (!sameId(c.id, request.collection_id)) return c;
    const requests = c.requests || [];
    if (requests.some((r) => sameId(r.id, request.id))) return c;
    return { ...c, requests: [...requests, request] };
  });
}

export function applyRequestUpdated(collections, payload) {
  const incoming = payload?.request;
  if (!incoming || incoming.id == null) return collections;
  // Keep the receiver's prior edit right rather than the actor's broadcast value.
  const prior = priorRequestCanEdit(collections, incoming.id);
  const request = withCanEdit(hydrateRequestFromApi(incoming), prior);
  // A request can be moved to another collection, so remove it wherever it
  // currently lives and re-insert it into its current collection.
  return collections.map((c) => {
    const requests = c.requests || [];
    const without = requests.filter((r) => !sameId(r.id, request.id));
    if (sameId(c.id, request.collection_id)) {
      return { ...c, requests: [...without, request] };
    }
    return without.length === requests.length ? c : { ...c, requests: without };
  });
}

export function applyRequestDeleted(collections, payload) {
  const id = payload?.request_id;
  if (id == null) return collections;
  return collections.map((c) => {
    const requests = c.requests || [];
    const next = requests.filter((r) => !sameId(r.id, id));
    return next.length === requests.length ? c : { ...c, requests: next };
  });
}

export function applyCollectionVariables(collections, payload) {
  const id = payload?.collection_id;
  if (id == null) return collections;
  const scopes = payload.variable_scopes || {};
  return collections.map((c) => {
    if (!sameId(c.id, id)) return c;
    return {
      ...c,
      variable_scopes: scopes,
      settings: {
        ...(c.settings || {}),
        collection_variables:
          scopes.collection_variables ?? c.settings?.collection_variables ?? [],
      },
    };
  });
}

// The sidebar_layout_updated payload is built server-side with the ACTOR's
// permissions: filtered to collections the actor can access and stamped with
// the actor's `can_edit`. It must therefore never wholesale-replace the
// receiver's state. Merge instead: adopt the broadcast layout (sort_order,
// folders, request placement) for collections the receiver already has while
// keeping the receiver's own `can_edit` values, and keep receiver-visible
// collections/requests the actor's filtered payload omits. Entities unknown
// to the receiver get the workspace-level right, same as the create events.
export function applySidebarLayout(collections, payload, canEdit) {
  const incoming = payload?.collections;
  if (!Array.isArray(incoming)) return collections;

  const localById = new Map(collections.map((c) => [String(c.id), c]));
  const incomingIds = new Set();

  const merged = incoming
    .filter((c) => c && c.id != null)
    .map((c) => {
      incomingIds.add(String(c.id));
      const hydrated = hydrateCollectionFromApi(c);
      const local = localById.get(String(c.id));
      if (!local) return withCanEdit(hydrated, canEdit);

      const localRequestsById = new Map(
        (local.requests || []).map((r) => [String(r.id), r])
      );
      const seenRequestIds = new Set();
      const requests = (hydrated.requests || []).map((r) => {
        seenRequestIds.add(String(r.id));
        const prior = localRequestsById.get(String(r.id));
        return withCanEdit(r, prior ? prior.can_edit : canEdit);
      });
      for (const r of local.requests || []) {
        if (!seenRequestIds.has(String(r.id))) requests.push(r);
      }

      const { can_edit, ...rest } = hydrated;
      return { ...local, ...rest, requests };
    });

  for (const c of collections) {
    if (!incomingIds.has(String(c.id))) merged.push(c);
  }
  return merged;
}
